import type { UploadedFile } from 'models';
import { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'ui';
import { useUploadFile } from 'features/files';

type P = {
  onLoadingUpdate: (uploading: boolean) => void;
  onUpload: (files: UploadedFile[]) => void;
};

const AttachmentMenuFileInput = forwardRef<HTMLInputElement, P>(({ onLoadingUpdate, onUpload }, ref) => {
  const { t } = useTranslation();
  const { mutateAsync: uploadFile } = useUploadFile();

  const handleFiles = useCallback(
    async (files: FileList) => {
      const uploadedFiles: UploadedFile[] = [];
      const promises = [];

      for (let index = 0; index < files.length; index++) {
        const file = files.item(index);

        if (file) {
          promises.push(
            uploadFile(
              {
                file,
              },
              {
                onError() {
                  // Errors are handled per files later
                },
              },
            ),
          );
        }
      }

      const responses = await Promise.allSettled(promises);

      for (const [index, result] of responses.entries()) {
        const file = files.item(index);
        if (!file) {
          continue;
        }

        if (result.status === 'fulfilled') {
          uploadedFiles.push(result.value);
        } else {
          message.error(t(`Something went wrong. File {{fileName}} couldn't be uploaded.`, { fileName: file.name }));
          console.error('Upload failed', result);
        }
      }

      return uploadedFiles;
    },
    [t, uploadFile],
  );

  return (
    <input
      className="attachment-menu-file-input"
      multiple
      onChange={async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
          return;
        }

        onLoadingUpdate(true);

        try {
          const uploadedFiles = await handleFiles(files);
          onUpload(uploadedFiles);
        } finally {
          onLoadingUpdate(false);
          // eslint-disable-next-line require-atomic-updates -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          event.target.value = '';
        }
      }}
      ref={ref}
      type="file"
    />
  );
});

export default AttachmentMenuFileInput;
